import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle2 } from 'lucide-react';
import ModalShell, { ModalFooter, MODAL_SUBMIT_CLASS } from '../common/ModalShell';
import { submitContactInquiry } from '../../redux/contactInquirySlice';

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
// A mobile number is OPTIONAL here, so "empty" is valid and only a number that
// was actually typed is checked. 6–15 digits once formatting is stripped —
// the same rule the backend applies to contact_inquiries.phone and the same
// one Login uses on a mobile identifier, so a number accepted on one screen is
// never refused on the other.
const digitsOf = (s) => String(s || '').replace(/[^0-9]/g, '');
const isPhoneOk = (s) => {
  const d = digitsOf(s);
  return d.length === 0 || (d.length >= 6 && d.length <= 15);
};

// Contact-sales modal for a custom (non-purchasable) plan. Works on the doctor
// page and the public landing page. On submit it records a contact inquiry and
// shows a thank-you message.
//
// WHY A PHONE FIELD (22 Sep 2026): these inquiries are answered by a person,
// and on this market a mobile number is the contact that gets picked up. The
// backend takes it as an optional column and demands at least one of
// e-mail/phone; this form still asks for the e-mail, because the thank-you
// screen promises a reply to it, and treats the number as the extra.
const ContactModal = ({ pkg, source = 'landing', doctorId = null, defaultName = '', defaultEmail = '', onClose }) => {
  const dispatch = useDispatch();
  const submitting = useSelector((s) => s.contactInquiry.submitting);
  const [form, setForm] = useState({ name: defaultName, email: defaultEmail, phone: '', notes: '' });
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const valid = form.name.trim() && isEmail(form.email) && isPhoneOk(form.phone);

  const submit = async () => {
    if (!valid) {
      setErr(
        !isPhoneOk(form.phone)
          ? 'Enter a valid mobile number (6–15 digits), or leave it blank.'
          : 'Please enter your name and a valid email.'
      );
      return;
    }
    setErr('');
    try {
      await dispatch(submitContactInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        // Sent only when given — an empty string is not "no phone" to a
        // validator that is handed one.
        ...(digitsOf(form.phone) ? { phone: digitsOf(form.phone) } : {}),
        notes: form.notes.trim(),
        packageId: pkg?.id || null,
        packageName: pkg?.name || null,
        doctorId,
        source,
      })).unwrap();
      setDone(true);
    } catch (e) {
      setErr(typeof e === 'string' ? e : 'Could not send your request — please try again.');
    }
  };

  const field = 'w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

  return (
    // Click on the dimmed page closes, as before the shared frame.
    <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) onClose(); }}>
    <ModalShell
      title={`Contact us${pkg?.name ? ` — ${pkg.name}` : ''}`}
      onClose={onClose}
      size="md"
      zClass="z-[110]"
      testId="contact-modal"
      footer={done ? (
        <button type="button" onClick={onClose} className={MODAL_SUBMIT_CLASS}>
          Done
        </button>
      ) : (
        <ModalFooter
          onCancel={onClose}
          onSubmit={submit}
          submitLabel={submitting ? 'Sending…' : 'Submit'}
          busy={submitting}
          disabled={!valid}
          submitTestId="contact-submit"
        />
      )}
    >
        {done ? (
          <div className="py-4 flex flex-col items-center text-center gap-3" data-testid="contact-success">
            <span className="p-3 rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 className="w-8 h-8" /></span>
            <h3 className="text-lg font-bold text-[#1a365d]">Thank you for contacting us</h3>
            <p className="text-sm text-gray-600">
              Our Mitra One Care team will reach out to <strong>{form.email}</strong> shortly to discuss this plan.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Tell us a little about your needs and we&apos;ll get back to you about <strong>{pkg?.name || 'a custom plan'}</strong>.
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Name</label>
                <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} placeholder="Your name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} placeholder="you@example.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Mobile number <span className="font-normal text-gray-400">(optional)</span></label>
                <input data-testid="contact-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} placeholder="9876543210" />
                {/* Submit is disabled while the form is invalid, so a bad
                    number has to say why here — a greyed-out button with no
                    reason beside it is a dead end. */}
                {!isPhoneOk(form.phone) && (
                  <p className="mt-1 text-xs text-red-600" data-testid="contact-phone-error">
                    Enter a valid mobile number (6–15 digits), or leave it blank.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Additional notes</label>
                <textarea data-testid="contact-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={field} placeholder="What are you looking for? (team size, organisation, requirements…)" />
              </div>
              {err && <p className="text-xs text-red-600" data-testid="contact-error">{err}</p>}
            </div>
          </>
        )}
    </ModalShell>
    </div>
  );
};

export default ContactModal;
